/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'

import PropTypes from 'prop-types'

import {Badge, Box, Image, Text} from '@chakra-ui/react'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'

import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'

import {useItemVariant} from '.'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, ...props}) => {
    const variant = useItemVariant()
    const {tCart} = useTranslationHelpers()

    const image = findImageGroupBy(variant?.imageGroups, {
        viewType: 'hi-res-grey',
        selectedVariationAttributes: variant?.variationValues
    })?.images?.[0]

    // Get the final image URL
    const imageUrl = image?.disBaseLink
        ? `${image.disBaseLink}?sw=150&sh=150&sm=fit`
        : image?.link
        ? `${image.link}?sw=150&sh=150&sm=fit`
        : null

    return (
        <Box width="84px" height="105px" {...props}>
            <Box position="relative">
                {variant?.c_isSale && (
                    <Badge
                        position="absolute"
                        top={0}
                        left={0}
                        marginLeft={2}
                        marginTop={2}
                        fontSize="10px"
                        variant="solid"
                        colorScheme="blue"
                    >
                        {tCart?.summaryImageLabelSale}
                    </Badge>
                )}

                {imageUrl ? (
                    <>
                        <Image
                            alt={image?.alt || variant.name || variant.productName}
                            src={imageUrl}
                            crossOrigin="anonymous"
                            fallback={
                                <img
                                    src={imageUrl}
                                    alt={image?.alt || variant.name || variant.productName}
                                    style={{
                                        width: '100%',
                                        height: '100%'
                                    }}
                                    onError={(e) => {
                                        console.error('Regular img failed to load:', imageUrl, e)
                                    }}
                                />
                            }
                            {...imageProps}
                        />
                    </>
                ) : (
                    <Box
                        bg="gray.100"
                        w="100%"
                        h="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Text fontSize="xs" color="gray.500">
                            {tCart?.summaryNoImageLabel}
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object,
    ratio: PropTypes.number
}

export default ItemImage
